import { useMemo } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import type { SeriesLegendItemClickEventObject } from "highcharts/highcharts.src";
import type { MetricConfig } from "../../../modules/merchandising/utils/metrics";
import { trellisPalette } from "./palettes";
import { useDates } from "../../../dates/DatesContext";
import MultiMetricChart from "../merchandise/MultiMetricChart";
import { apiClient } from "../../../zodios/client";
import useSelectedMetrics from "../merchandise/hooks/useSelectedMetrics";

const groupToLegacyGroup = {
  day: "daily",
  week: "weekly",
  month: "monthly",
} as const;

interface ChartDataRow {
  start_date: string;
  position_avg?: number;
  share_of_shelf?: number;
  org_share_of_shelf?: number;
  share_of_voice?: number;
  atf_share_of_shelf?: number;
  atf_org_share_of_shelf?: number;
  atf_share_of_voice?: number;
}

const AnalyticsOverviewChart = () => {
  const { group, start, end } = useDates();

  const shelfStatsQuery = useQuery({
    queryKey: ["shelf_timeline", start.toISOString(), end.toISOString(), group],
    queryFn: async () =>
      await apiClient.getShelfStats({
        queries: {
          stats_by: "time_series",
          end: end.toISOString(),
          start: start.toISOString(),
          period: groupToLegacyGroup[group],
        },
      }),
    gcTime: 0,
    staleTime: 0,
  });

  // Transform data to combine page_1 and above_fold data by date
  const chartData = useMemo(() => {
    if (!shelfStatsQuery.data) return [];

    const page1Data = shelfStatsQuery.data.filter((item) => item.rollup_type === "page_1");
    const atfData = shelfStatsQuery.data.filter((item) => item.rollup_type === "above_fold");

    // Create a map of dates to combined data
    const dataByDate = new Map<string, ChartDataRow>();

    page1Data.forEach((item) => {
      dataByDate.set(item.date, {
        start_date: item.date,
        position_avg: item.position_avg,
        share_of_shelf: item.share_of_shelf,
        org_share_of_shelf: item.org_share_of_shelf,
        share_of_voice: item.share_of_voice,
      });
    });

    atfData.forEach((item) => {
      const existing = dataByDate.get(item.date) || { start_date: item.date };
      dataByDate.set(item.date, {
        ...existing,
        atf_share_of_shelf: item.share_of_shelf,
        atf_org_share_of_shelf: item.org_share_of_shelf,
        atf_share_of_voice: item.share_of_voice,
      });
    });

    return Array.from(dataByDate.values())
      .sort((a, b) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf())
      .slice(-30); // Keep only last 30 data points
  }, [shelfStatsQuery.data]);

  const metrics: MetricConfig<ChartDataRow>[] = useMemo(
    () => [
      {
        id: "position_avg",
        name: ($) => $.metrics["Average Position"],
        color: trellisPalette[3],
        accessor: (d) => d?.position_avg ?? null,
        axis: 1,
        chartType: "column",
        format: "number",
        decimalPlaces: 2,
      },
      {
        id: "share_of_shelf",
        name: ($) => $.metrics["SOS"],
        color: trellisPalette[0],
        accessor: (d) => d?.share_of_shelf ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
      {
        id: "org_share_of_shelf",
        name: ($) => $.metrics["SOS Organic"],
        color: trellisPalette[0],
        accessor: (d) => d?.org_share_of_shelf ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
      {
        id: "share_of_voice",
        name: ($) => $.metrics["SOV"],
        color: trellisPalette[2],
        accessor: (d) => d?.share_of_voice ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
      {
        id: "atf_share_of_shelf",
        name: ($) => $.metrics["ATF SOS"],
        color: trellisPalette[1],
        accessor: (d) => d?.atf_share_of_shelf ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
      {
        id: "atf_org_share_of_shelf",
        name: ($) => $.metrics["ATF SOS Organic"],
        color: trellisPalette[1],
        accessor: (d) => d?.atf_org_share_of_shelf ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
      {
        id: "atf_share_of_voice",
        name: ($) => $.metrics["ATF SOV"],
        color: trellisPalette[4],
        accessor: (d) => d?.atf_share_of_voice ?? null,
        axis: 0,
        chartType: "line",
        format: "percent",
        decimalPlaces: 2,
      },
    ],
    [],
  );

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    metrics.map((m) => m.id),
    "analyticsOverview",
  );

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  return (
    <MultiMetricChart
      data={chartData}
      metrics={metrics}
      selectedMetrics={selectedMetrics}
      handleLegendChange={handleLegendChange}
      group={group}
      loading={shelfStatsQuery.isLoading}
      overrideHeight={400}
      showAnnotations={true}
    />
  );
};

export default AnalyticsOverviewChart;
